import { uniqBy, isEmpty } from "lodash"
import {
  getLicenseWhereId,
  getLicenseWhereAccountId,
  getLicenseWhereDistrictId,
  getAllLicenses,
  getSetByLicensable,
  getLicensesSetsByLicensable
} from "../../../../mp-database"
import SiteLicense from "../../../../mp-database/siteLicenses/SiteLicense"
import { SiteLicenseType } from "../../../../mp-database/siteLicenses/types"
import { checkStudentHasActiveOrgPoolLicense } from "../../../../../services/generic-services/org/students/checkStudentDistrictPoolLicense"

interface GetUserLicenseActions {
  getUserLicenseRequest: () => void
  getUserLicenseSuccess: (payload: { licenses: any[]; role: string }) => void
  getUserLicenseFailure: (err: any) => void
}

type GetActiveOrExpiredLicensesCallback = (licenses: any[] | null) => void

/**
 * Check if a license is an ACT license based on its ID and test category.
 * @param id - The license ID
 * @param testCategory - Optional test category to validate against
 * @returns True if it's an ACT license, false otherwise
 */
const isACTLicenseCheck = (
  id: string | undefined,
  program?: string
): boolean => {
  return (
    [
      "bract000-78c8-40da-9db8-e5f5e15b55af", // ACT Bell Ringers
      "01985b4c-3282-7c54-ac8c-34609a8163d6", // ACT Practice Test 1
      "01985b53-4d69-7c73-aebb-547cc468e73e", // ACT Practice Test 2
      "281eb7e0-c272-549b-bff2-83facff830b7", // ACT Practice Test 3
      "d1act000-f773-4ff2-b6eb-4dc38bd389d9", // ACT Diagnostic Test
      "a29ab9ba-4753-4ac0-b54a-560656cd9956" // ACT Study Plan
    ].includes(id) &&
    (!program || program === "EACT")
  )
}

/**
 * Generate an ACT seat license if the license is valid and the user has the appropriate license type.
 * @param license - The license object containing id, name, navCategory, navName, and licenseTypes
 * @param user - The user object containing role and id
 * @returns The seat license object if valid, null otherwise
 */
const generateACTSeatLicense = (license: any, user: any) => {
  // First verify the license is a valid ACT license
  if (!isACTLicenseCheck(license.id, license?.program)) {
    return null
  }

  // Then create the seat license if the user's role matches the license types
  const licenseType = user.role === "Student" ? "student" : "educator"
  if (license.licenseTypes?.includes(licenseType)) {
    return {
      id: license.id, // same as licenseRegistryId
      name: license.name, // from sets/licenseSets doc
      navCategory: license.navCategory, // from sets/licenseSets doc
      navName: license.navName, // from sets/licenseSets doc
      licenseSet: license.id, // same as licenseRegistryId
      licenseType: licenseType,
      licensedUsers: {
        [user.id]: true
      },
      isExpired: false
    }
  }

  return null
}

export default async function getActiveOrExpiredLicenses(
  user: any, // TODO: fix user type
  {
    getUserLicenseRequest,
    getUserLicenseSuccess,
    getUserLicenseFailure
  }: GetUserLicenseActions,
  callback?: GetActiveOrExpiredLicensesCallback
) {
  let licensesArr = []
  const licensePromise = []
  const actLicenses = []
  const hasUserACTRole =
    user.role === "ACT Admin" || user.role === "Organization Admin"

  getUserLicenseRequest()

  // Fetch license sets for all roles to populate actLicenses
  const setsQuery = getSetByLicensable()
  const licensesSetsQuery = getLicensesSetsByLicensable()

  try {
    const [setsDocs, licensesSetsDocs] = await Promise.all([
      setsQuery,
      licensesSetsQuery
    ])

    // Populate actLicenses for all roles
    setsDocs.forEach(docSnapshot => {
      const docData = docSnapshot.data
      if (docData && !docData.deleted) {
        // const { id, name, navCategory, navName, testCategory } = docData
        const actSeatLicense = generateACTSeatLicense(
          {
            ...docData,
            licenseTypes: ["student", "educator"],
            program: docData.testCategory
          },
          user
        )

        if (actSeatLicense) {
          actLicenses.push(actSeatLicense)
        }
      }
    })

    licensesSetsDocs.forEach(docSnapshot => {
      const docData = docSnapshot.data
      if (docData && !docData.deleted) {
        const actSeatLicense = generateACTSeatLicense(docData, user)
        if (actSeatLicense) {
          actLicenses.push(actSeatLicense)
        }
      }
    })

    // Now handle role-specific logic for the four special roles
    if (
      user.role === "Super Administrator" ||
      user.role === "Sales Rep" ||
      user.role === "ACT Admin" ||
      user.role === "Organization Admin"
    ) {
      const possibleLicenses = []

      setsDocs.forEach(docSnapshot => {
        const docData = docSnapshot.data
        if (docData && !docData.deleted) {
          const { id, name, navCategory, navName, testCategory } = docData
          const isACTLicense = isACTLicenseCheck(id, testCategory)
          const license = {
            id,
            name,
            navCategory,
            navName,
            licenseSet: id,
            licenseTypes: ["student", "educator"]
          }

          if (hasUserACTRole) {
            if (isACTLicense) {
              // For ACT roles, only include ACT and EACT licenses
              possibleLicenses.push(license)
            } else {
              // noop - ACT roles do not get non-ACT licenses
            }
          } else {
            // Super Admin and Sales Rep get all licenses
            possibleLicenses.push(license)
          }
        }
      })

      licensesSetsDocs.forEach(docSnapshot => {
        const docData = docSnapshot.data
        if (docData && !docData.deleted) {
          const isACTLicense = isACTLicenseCheck(docData.id, docData.program)

          if (hasUserACTRole) {
            if (isACTLicense) {
              // For ACT roles, only include ACT and EACT licenses
              possibleLicenses.push(docData)
            } else {
              // noop - ACT roles do not get non-ACT licenses
            }
          } else {
            // Super Admin and Sales Rep get all licenses
            possibleLicenses.push(docData)
          }
        }
      })

      const sortedLicenses = uniqBy(possibleLicenses, "id").sort((a, b) => {
        const nameA = a.displayName || a.name
        const nameB = b.displayName || b.name
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      })

      getUserLicenseSuccess({
        licenses: sortedLicenses,
        role: user.role ?? ""
      })
      if (callback) callback(sortedLicenses)
      return
    }
  } catch (err) {
    console.error("Error fetching license sets:", err)
    // Continue with the rest of the function even if fetching license sets fails
  }

  // IMPORTANT NOTE
  // Super Administrator, Sales Rep, ACT Admin, Organization Admin have been handled above
  // And don't need to be processed below
  // District Admin, School Admin, Teacher, Student, and other roles will be processed below

  const schoolSiteLicenseService = new SiteLicense()
  const schoolSiteLicense = await schoolSiteLicenseService.load(
    user.school,
    SiteLicenseType.SCHOOL
  )
  const districtSiteLicenseService = new SiteLicense()
  const districtSiteLicense = await districtSiteLicenseService.load(
    user.schoolDistrict,
    null
  )

  // Check if the user is a student in an ACT district and filter licenses accordingly
  let actStudentLicenses = []

  if (user.role === "Student" && user.id) {
    try {
      let hasActiveLicense = false

      // Check district pool if user has a district
      if (user.schoolDistrict) {
        hasActiveLicense = await checkStudentHasActiveOrgPoolLicense(
          user.id,
          user.schoolDistrict,
          "DISTRICT"
        )
      }

      // If no district pool, check school pool
      if (!hasActiveLicense && user.school) {
        hasActiveLicense = await checkStudentHasActiveOrgPoolLicense(
          user.id,
          user.school,
          "SCHOOL"
        )
      }

      if (hasActiveLicense) {
        actStudentLicenses = actLicenses
      }
    } catch (error) {
      console.error("Failed to check student license status:", error)
    }
  }

  if (schoolSiteLicense.enabled) {
    const licenses = await schoolSiteLicenseService.getLegacySeatLicenses(user)

    if (user.role === "Student") {
      // Inject AOP licenses if user is a student in an AOP pool
      licenses.push(...actStudentLicenses)
    } else {
      // Inject ACT and EACT licenses if user is NOT a student in an ACT district
      licenses.push(...actLicenses)
    }

    getUserLicenseSuccess({
      licenses: licenses,
      role: user.role ?? ""
    })
    if (callback) callback(licenses)

    return
  } else if (districtSiteLicense.enabled) {
    const licenses = await districtSiteLicenseService.getLegacySeatLicenses(
      user
    )

    if (user.role === "Student") {
      // Inject AOP licenses if user is a student in an AOP pool
      licenses.push(...actStudentLicenses)
    } else {
      // Inject ACT and EACT licenses if user is NOT a student in an ACT district
      licenses.push(...actLicenses)
    }

    getUserLicenseSuccess({
      licenses: licenses,
      role: user.role ?? ""
    })
    if (callback) callback(licenses)

    return
  } else if (user.licenses) {
    if (user.role === "Student") {
      Object.entries(user.licenses).forEach(([licenseId, active]) => {
        if (active) {
          const dataLicenses = getLicenseWhereId(licenseId)
          licensePromise.push(dataLicenses)
        }
      })
    } else {
      const { schoolDistrict, school, role } = user

      if (role === "District Admin" && schoolDistrict) {
        licensePromise.push(getLicenseWhereDistrictId(schoolDistrict))
      } else {
        if (schoolDistrict) {
          licensePromise.push(getLicenseWhereAccountId(schoolDistrict))
        }
        if (school) {
          licensePromise.push(getLicenseWhereAccountId(school))
        }
      }
    }
  } else {
    // If no licenses in user object, fetch based on role
    if (user.role !== "Student") {
      const { schoolDistrict, school, role } = user

      if (role === "District Admin" && schoolDistrict) {
        licensePromise.push(getLicenseWhereDistrictId(schoolDistrict))
      } else {
        if (schoolDistrict) {
          licensePromise.push(getLicenseWhereAccountId(schoolDistrict))
        }
        if (school) {
          licensePromise.push(getLicenseWhereAccountId(school))
        }
      }
    }
  }

  return Promise.all(licensePromise)
    .then(results => {
      results.forEach(querySnapshot => {
        querySnapshot.forEach(docSnapshot => {
          const docData =
            typeof docSnapshot.data === "function"
              ? docSnapshot.data()
              : docSnapshot.data
          if (docData && !docData.deleted) {
            licensesArr.push(docData)
          }
        })
      })

      // Sort by endDate descending
      licensesArr.sort((a, b) => b.endDate - a.endDate)

      // Note that some roles have been handled above
      if (user.role !== "Student") {
        licensesArr = licensesArr.filter(lic => {
          if (!isEmpty(lic.licensedUsers)) {
            const isTruScoreLicenseExist =
              (lic.navCategory === "TruScore Testing" &&
                lic.licenseType === "student") ||
              (lic.navCategory === "eBooks" && lic.licenseType === "student")

            const isStudyHallLicenseExist =
              lic.navCategory === "Study Hall" && lic.licenseType === "student"

            const isSnapLicenseExist =
              lic.navCategory === "snap" && lic.licenseType === "student"

            const licenseExists =
              (isStudyHallLicenseExist ||
                isTruScoreLicenseExist ||
                isSnapLicenseExist) &&
              Object.values(lic.licensedUsers).some(val => val)

            if (licenseExists) {
              return lic
            } else {
              // If it's not one of the special categories above,
              // filter by whether the user specifically has it
              return lic.licensedUsers[user.id as string]
            }
          }
          return false
        })
      }

      if (user.role === "Student") {
        // Inject AOP licenses if user is a student in an AOP pool
        licensesArr.push(...actStudentLicenses)
      } else {
        // Inject ACT and EACT licenses if user is NOT a student in an ACT district
        licensesArr.push(...actLicenses)
      }
      return uniqBy(licensesArr, "licenseSet").sort((a, b) => {
        const nameA = a.displayName || a.name
        const nameB = b.displayName || b.name
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      })
    })
    .then(_licenses => {
      getUserLicenseSuccess({
        licenses: _licenses,
        role: user.role ?? ""
      })
      if (callback) callback(_licenses)
    })
    .catch(err => {
      getUserLicenseFailure(err)
      if (callback) callback(null)
    })
}
